import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { callCentrePageTitle } from '../call-centre/callCentreNav';

/**
 * The Call Centre window's open / minimised state — the same behaviour the Leads
 * Tracker and Content Studio windows have: it remembers where you were, minimise
 * returns you to the page you came from, and the launcher in the top bar resumes it.
 */
const KEY = 'call-centre-window';
const HOME = '/call-centre';
const DEFAULT = { open: false, lastPath: HOME, returnPath: '/dashboard', mode: 'max' };

const inWindow = (path) => String(path || '').startsWith(HOME);
const resumePath = (path) => (inWindow(path) ? path : HOME);

function load() {
  try {
    const stored = { ...DEFAULT, ...JSON.parse(localStorage.getItem(KEY) || '{}') };
    return { ...stored, lastPath: resumePath(stored.lastPath) };
  } catch {
    return { ...DEFAULT };
  }
}

const CallCentreWindowContext = createContext(null);

export function CallCentreWindowProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState(load);
  const prevPath = useRef(location.pathname);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    const full = location.pathname + location.search;
    const here = inWindow(location.pathname);
    setState((s) => {
      if (!here) return s;
      const cameFromMain = !inWindow(prevPath.current);
      return { ...s, open: true, lastPath: full, returnPath: cameFromMain ? prevPath.current : s.returnPath };
    });
    prevPath.current = location.pathname;
  }, [location]);

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* ignore */ }
  }, [state]);

  const minimize = useCallback(() => {
    if (inWindow(location.pathname)) {
      setState((s) => ({ ...s, open: true, lastPath: location.pathname + location.search }));
    }
    navigate(stateRef.current.returnPath || '/dashboard');
  }, [location, navigate]);

  const close = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
    navigate(stateRef.current.returnPath || '/dashboard');
  }, [navigate]);

  const restore = useCallback(() => navigate(resumePath(stateRef.current.lastPath)), [navigate]);

  const openCallCentre = useCallback(() => {
    const path = resumePath(stateRef.current.lastPath);
    setState((s) => ({ ...s, open: true, lastPath: path }));
    navigate(path);
  }, [navigate]);

  const toggleMode = useCallback(() => {
    setState((s) => ({ ...s, mode: s.mode === 'max' ? 'window' : 'max' }));
  }, []);

  const value = {
    ...state,
    stepLabel: callCentrePageTitle(state.lastPath?.split('?')[0] || ''),
    onCallCentre: inWindow(location.pathname),
    minimize,
    close,
    restore,
    openCallCentre,
    toggleMode,
  };

  return <CallCentreWindowContext.Provider value={value}>{children}</CallCentreWindowContext.Provider>;
}

export function useCallCentreWindow() {
  return useContext(CallCentreWindowContext) || {
    open: false, onCallCentre: false, mode: 'max', lastPath: HOME, stepLabel: 'Call Centre',
    minimize() {}, close() {}, restore() {}, openCallCentre() {}, toggleMode() {},
  };
}
